import { Link } from 'react-router'
import { Avatar } from '../avatar/Avatar.tsx'
import { TrustGauge } from '../trust-gauge/TrustGauge.tsx'
import type { EmbedData, MatchEmbedData, PlayerEmbedData, TrustSegment } from './types.ts'
import styles from './ArticleEmbed.module.css'

export interface ArticleEmbedProps {
  embed: EmbedData
  className?: string
}

/** A live Carousel card inside an article: a match result, a player, or a Trust Score. */
export function ArticleEmbed({ embed, className }: ArticleEmbedProps) {
  const classes = [styles.embed, className].filter(Boolean).join(' ')
  if (embed.kind === 'match' && embed.match) return <MatchCard match={embed.match} className={classes} />
  if (embed.player) {
    return <PlayerCard player={embed.player} trust={embed.kind === 'trust' ? embed.trust : undefined} className={classes} />
  }
  return null
}

function matchLabel(m: MatchEmbedData): string {
  const bestOf = m.best_of > 1 ? ` · Best of ${m.best_of}` : ''
  switch (m.status) {
    case 'completed': return `Final${bestOf}`
    case 'forfeit': return 'Final · Forfeit'
    case 'live': return `Live${bestOf}`
    case 'cancelled': return 'Cancelled'
    default: {
      const d = new Date(m.starts_at)
      return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) + ' · ' +
        d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) + bestOf
    }
  }
}

function MatchCard({ match, className }: { match: MatchEmbedData; className: string }) {
  const [home, away] = match.teams
  const scored = match.teams.some((t) => t.score !== null)
  const context = [match.game, match.season_name, match.org_name].filter(Boolean).join(' · ')

  return (
    <figure className={className} aria-label={`Match: ${match.teams.map((t) => t.name).join(' vs ')}`}>
      <div className={styles.head}>
        <span className={match.status === 'live' ? styles.live : styles.label}>{matchLabel(match)}</span>
      </div>
      <div className={styles.scoreline}>
        {home && <Side team={home} align="start" />}
        <span className={styles.score}>
          {scored ? `${home?.score ?? 0} – ${away?.score ?? 0}` : 'vs'}
        </span>
        {away && <Side team={away} align="end" />}
      </div>
      <figcaption className={styles.foot}>
        <span>{context}</span>
        <Link to={`/org/${match.org_slug}/schedule/${match.id}`} className={styles.more}>Match details →</Link>
      </figcaption>
    </figure>
  )
}

function Side({ team, align }: { team: MatchEmbedData['teams'][number]; align: 'start' | 'end' }) {
  return (
    <div className={[styles.side, align === 'end' ? styles.end : '', team.winner ? styles.winner : ''].filter(Boolean).join(' ')}>
      <Avatar src={team.logo_url || undefined} fallback={team.name} alt="" size="md" />
      <span className={styles.team}>{team.name}</span>
    </div>
  )
}

function PlayerCard({ player, trust, className }: { player: PlayerEmbedData; trust?: TrustSegment[]; className: string }) {
  const name = player.gamer_tag || player.username
  return (
    <figure className={className} aria-label={`Player: ${name}`}>
      <div className={styles.player}>
        <Avatar src={player.avatar_url ?? undefined} fallback={name} alt="" size="lg" />
        <div className={styles.playerText}>
          <Link to={`/u/${encodeURIComponent(player.username)}`} className={`${styles.playerName} ${styles.profileLink}`}>{name}</Link>
          <span className={styles.handle}>@{player.username}</span>
        </div>
        <div className={styles.trust}>
          <TrustGauge score={player.trust_score} size={trust ? 72 : 48} />
          <span className={styles.level}>{player.trust_level}</span>
        </div>
      </div>
      {trust && trust.length > 0 && (
        <ul className={styles.segments} aria-label="Trust Score breakdown">
          {trust.map((s) => (
            <li key={s.key} className={styles.segment}>
              <span className={styles.segmentLabel}>{s.label}</span>
              <span className={styles.bar} aria-hidden="true">
                <span className={styles.fill} style={{ width: `${s.max ? Math.round((s.score / s.max) * 100) : 0}%` }} />
              </span>
              <span className={styles.segmentScore}>{s.score}/{s.max}</span>
            </li>
          ))}
        </ul>
      )}
      <figcaption className={styles.foot}>
        <span>Verified on Carousel</span>
        <Link to={`/u/${player.username}`} className={styles.more}>View profile →</Link>
      </figcaption>
    </figure>
  )
}
